"use client";
import { YnsLink } from "@/ui/yns-link";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { faBluesky, faInstagram, faTwitter, faWhatsapp, faYoutube } from "@fortawesome/free-brands-svg-icons";
import { faEnvelope } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { motion } from "framer-motion";

// Define a type for the social media items
interface SocialItem {
	name: string;
	icon: IconDefinition;
	href: string;
}

const socials: SocialItem[] = [
	{
		name: "WhatsApp",
		icon: faWhatsapp,
		href: "https://api.whatsapp.com/send?phone=34642367972&text=%C2%A1Socorro!%20%C2%A1Me%20persigue%20un%20drag%C3%B3n!",
	},
	{
		name: "Instagram",
		icon: faInstagram,
		href: "https://instagram.com/losamosdelcalabozo",
	},
	{
		name: "Twitter",
		icon: faTwitter,
		href: "https://twitter.com/amosdelcalabozo",
	},
	{
		name: "YouTube",
		icon: faYoutube,
		href: "https://www.youtube.com/@losamosdelcalabozo2205",
	},
	{
		name: "Email",
		icon: faEnvelope,
		href: "mailto:ayuda@losamosdelcalabozo.com?subject=%C2%A1Socorro!%20%C2%A1Ayuda!%20%C2%A1Me%20persigue%20un%20drag%C3%B3n!",
	},
	{
		name: "Bluesky",
		icon: faBluesky,
		href: "https://bsky.app/profile/losamosdelcalabozo.bsky.social",
	},
];

const links = [
	{
		label: "Sistemas",
		href: "/sistemas",
	},
	{
		label: "Aventuras",
		href: "/category/aventuras",
	},
	{
		label: "Precios",
		href: "/category/precios",
	},
	{
		label: "FAQ",
		href: "/faq",
	},
];

export async function Footer() {
	//   const t = await getTranslations("Global.footer");

	// Animation variants for social icons
	const iconVariants = {
		initial: {
			y: 0,
			rotate: 0,
			scale: 1,
			filter: "drop-shadow(0 0 0px transparent)",
		},
		hover: (custom: number) => ({
			y: [-5, 0, -5],
			rotate: [-5, 0, 5, 0],
			scale: [1, 1.2, 1],
			filter: "drop-shadow(0 0 8px #ff0000)",
			transition: {
				y: {
					duration: 1,
					repeat: Infinity,
					repeatType: "loop",
					ease: "easeInOut",
					delay: custom * 0.1,
				},
				rotate: {
					duration: 1.2,
					repeat: Infinity,
					repeatType: "loop",
					ease: "easeInOut",
					delay: custom * 0.1,
				},
				scale: {
					duration: 0.5,
					ease: "easeOut",
				},
				filter: {
					duration: 0.3,
				},
			},
		}),
	};

	return (
		<footer className="w-full bg-black text-white p-6 bg-gradient-to-b from-gray-900 to-black md:py-12 border-t-2 border-red-800">
			<div className="container flex max-w-7xl flex-col md:flex-row flex-wrap justify-center gap-12 text-sm sm:justify-between mx-auto">
				{/* Logo and contact section */}
				<div className="flex flex-col max-w-xs gap-4">
					<motion.div
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.6 }}
						className="mb-4"
					>
						<h2 className="font-amarante text-3xl mb-2 text-white">
							<span className="inline-block pb-1 border-b-2 border-red-700">Los Amos del Calabozo</span>
						</h2>
						<p className="text-gray-300 mt-4">Directores de Juego Profesionales en Madrid</p>
					</motion.div>

					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={{ delay: 0.3, duration: 0.6 }}
						className="mt-2"
					>
						<h3 className="font-amarante text-xl mb-2 text-white">Contacto</h3>
						<a
							href="mailto:contacto@losamosdelcalabozo.com"
							className="text-gray-300 hover:text-red-400 transition-colors mb-2 inline-block"
						>
							contacto@losamosdelcalabozo.com
						</a>
						<a href="/" className="text-gray-300 hover:text-red-400 transition-colors block">
							www.losamosdelcalabozo.com
						</a>
					</motion.div>
				</div>

				{/* Links section */}
				<motion.nav
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ delay: 0.2, duration: 0.6 }}
					className="flex flex-col gap-6"
				>
					<section>
						<h3 className="font-amarante text-xl mb-4 text-white border-b-2 border-red-700">Explora</h3>
						<ul role="list" className="grid gap-3">
							{links.map((link, index) => (
								<motion.li
									key={link.label}
									initial={{ opacity: 0, x: -20 }}
									animate={{ opacity: 1, x: 0 }}
									transition={{ delay: 0.3 + index * 0.1, duration: 0.5 }}
								>
									<YnsLink
										className="text-gray-300 hover:text-red-400 transition-colors font-amarante text-lg flex items-center"
										href={link.href}
									>
										<motion.span
											whileHover={{
												x: 5,
												textShadow: "1px 1px 3px rgba(255, 0, 0, 0.7)",
												transition: { duration: 0.2 },
											}}
										>
											{link.label}
										</motion.span>
									</YnsLink>
								</motion.li>
							))}
						</ul>
					</section>
				</motion.nav>

				{/* Social icons section */}
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ delay: 0.4, duration: 0.6 }}
					className="flex flex-col gap-4"
				>
					<h3 className="font-amarante text-xl mb-2 text-white border-b-2 border-red-700">Síguenos</h3>
					<div className="grid grid-cols-3 gap-6">
						{socials.map((social, index) => (
							<motion.a
								key={social.name}
								href={social.href}
								target="_blank"
								rel="noopener noreferrer"
								aria-label={social.name}
								className="p-3 bg-gray-900 rounded-full hover:bg-red-900 transition-colors duration-300 flex items-center justify-center relative group"
								initial="initial"
								whileHover="hover"
								animate="initial"
								custom={index}
								variants={iconVariants}
							>
								{social.icon && (
									<FontAwesomeIcon
										icon={social.icon}
										className="w-14 h-14 text-white"
										style={{ fontSize: "1.5rem" }}
									/>
								)}

								{/* Tooltip on hover */}
								<span className="absolute -top-12 left-1/2 transform -translate-x-1/2 bg-red-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">
									{social.name}
								</span>

								{/* Pulsing ring effect */}
								<motion.div
									className="absolute inset-0 rounded-full border-2 border-red-600/40"
									animate={{
										scale: [1, 1.4, 1],
										opacity: [0.4, 0.1, 0.4],
									}}
									transition={{
										duration: 2,
										repeat: Infinity,
										repeatType: "loop",
										ease: "easeInOut",
										delay: index * 0.1,
									}}
								/>
							</motion.a>
						))}
					</div>
				</motion.div>
			</div>

			{/* Copyright section */}
			<motion.div
				initial={{ opacity: 0 }}
				animate={{ opacity: 1 }}
				transition={{ delay: 0.7, duration: 0.6 }}
				className="container mt-12 flex max-w-7xl flex-col items-center justify-between gap-4 text-sm text-gray-400 md:flex-row mx-auto border-t border-red-900/30 pt-6"
			>
				<div>
					<p className="font-amarante">© {new Date().getFullYear()} Los Amos del Calabozo</p>
				</div>

				<motion.div
					className="flex items-center"
					whileHover={{
						textShadow: "0 0 8px rgba(255,0,0,0.6)",
						transition: { duration: 0.3 },
					}}
				>
					<div className="flex items-center gap-4">
						<YnsLink
							className="text-gray-400 hover:text-red-400 transition-colors"
							href="/politica-privacidad"
						>
							Política de Privacidad
						</YnsLink>
						<YnsLink className="text-gray-400 hover:text-red-400 transition-colors" href="/aviso-legal">
							Aviso legal
						</YnsLink>
					</div>
					<p className="text-red-500 pl-5 font-amarante">¡Que la aventura continúe!</p>
				</motion.div>
			</motion.div>

			{/* Decorative red line at the bottom */}
			<div className="w-full h-4 relative overflow-hidden">
				{Array.from({ length: 20 }).map((_, i) => (
					<motion.div
						key={i}
						className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-red-900/70 to-transparent"
						animate={{
							opacity: [0.3, 0.7, 0.3],
						}}
						transition={{
							duration: 4,
							repeat: Infinity,
							ease: "easeInOut",
						}}
					/>
				))}
			</div>
		</footer>
	);
}
